import { Milestone } from 'src/@types/graphql'
import { MILESTONE_TYPE } from 'src/shared/constants/milestone-constants'
import { DateUtils } from 'src/shared/utils/DateUtils'
import { ValidationForEditMilestoneDueDate } from 'src/screens/shared/implementation/details/milestone/MilestoneService'

export const getProjectValidationForContract = (
  milestones: Milestone[],
): ValidationForEditMilestoneDueDate | undefined => {
  const notContractMilestones = milestones.filter((item) => item.type !== MILESTONE_TYPE.CONTRACT)

  const firstMilestoneAfterContract = notContractMilestones.length > 0 ? notContractMilestones[0] : undefined

  if (firstMilestoneAfterContract == null) {
    return undefined
  }

  const maxDate = DateUtils.parseDate(DateUtils.parseAndFormatDate(firstMilestoneAfterContract.due_date))

  const message =
    firstMilestoneAfterContract.type === MILESTONE_TYPE.INTERMEDIATE_DISCUSSION
      ? 'validation.milestone.contract.due-date.after'
      : 'validation.date.invalid.range.after'

  return {
    message,
    maxDate: maxDate,
  }
}

export const getProjectValidationForIntermediateDiscussion = (
  milestone: Milestone,
  milestones: Milestone[],
  minContractDate?: Date,
  maxContractDate?: Date,
): ValidationForEditMilestoneDueDate | undefined => {
  const currentMilestoneYearInFocus = milestone.year_in_focus

  const lastYearFinancial = milestones.find(
    (item) => item.type === MILESTONE_TYPE.FINANCIAL_REPORT && item.year_in_focus === currentMilestoneYearInFocus - 1,
  )
  const nextYearAnnualReport = milestones.find(
    (item) => item.type === MILESTONE_TYPE.ANNUAL_REPORT && item.year_in_focus === currentMilestoneYearInFocus,
  )

  const minDate = lastYearFinancial
    ? DateUtils.parseDate(DateUtils.parseAndFormatDate(lastYearFinancial.due_date))
    : minContractDate
  const maxDate = nextYearAnnualReport
    ? DateUtils.parseDate(DateUtils.parseAndFormatDate(nextYearAnnualReport.due_date))
    : maxContractDate

  const message =
    !lastYearFinancial || !nextYearAnnualReport
      ? 'validation.date.invalid.range'
      : 'validation.milestone.immediate-discussion.due-date.is-not-between'

  if (minDate && maxDate) {
    return {
      message,
      minDate: minDate,
      maxDate: maxDate,
    }
  }
}

export const getProjectValidationForAnnualReport = (
  milestone: Milestone,
  milestones: Milestone[],
  minContractDate?: Date,
  maxContractDate?: Date,
): ValidationForEditMilestoneDueDate | undefined => {
  const currentMilestoneYearInFocus = milestone.year_in_focus
  const previousYearIntermediateDiscussion = milestones.find(
    (item) =>
      item.type === MILESTONE_TYPE.INTERMEDIATE_DISCUSSION && item.year_in_focus === currentMilestoneYearInFocus,
  )
  const nextYearIntermediateDiscussion = milestones.find(
    (item) =>
      item.type === MILESTONE_TYPE.INTERMEDIATE_DISCUSSION && item.year_in_focus === currentMilestoneYearInFocus + 1,
  )

  const minDate = previousYearIntermediateDiscussion
    ? DateUtils.parseDate(DateUtils.parseAndFormatDate(previousYearIntermediateDiscussion.due_date))
    : minContractDate
  const maxDate = nextYearIntermediateDiscussion
    ? DateUtils.parseDate(DateUtils.parseAndFormatDate(nextYearIntermediateDiscussion.due_date))
    : maxContractDate

  const message =
    !previousYearIntermediateDiscussion || !nextYearIntermediateDiscussion
      ? 'validation.date.invalid.range'
      : 'validation.milestone.annual-report.due-date.is-not-between'
  if (minDate && maxDate) {
    return {
      message: message,
      minDate: minDate,
      maxDate: maxDate,
    }
  }
}

export const getProjectValidationForFinancialReport = (
  milestone: Milestone,
  milestones: Milestone[],
  minContractDate?: Date,
  maxContractDate?: Date,
): ValidationForEditMilestoneDueDate | undefined => {
  const currentMilestoneYearInFocus = milestone.year_in_focus
  const previousYearIntermediateDiscussion = milestones.find(
    (item) =>
      item.type === MILESTONE_TYPE.INTERMEDIATE_DISCUSSION && item.year_in_focus === currentMilestoneYearInFocus,
  )
  const nextYearIntermediateDiscussion = milestones.find(
    (item) =>
      item.type === MILESTONE_TYPE.INTERMEDIATE_DISCUSSION && item.year_in_focus === currentMilestoneYearInFocus + 1,
  )

  const minDate = previousYearIntermediateDiscussion
    ? DateUtils.parseDate(DateUtils.parseAndFormatDate(previousYearIntermediateDiscussion.due_date))
    : minContractDate
  const maxDate = nextYearIntermediateDiscussion
    ? DateUtils.parseDate(DateUtils.parseAndFormatDate(nextYearIntermediateDiscussion.due_date))
    : maxContractDate

  const message =
    !previousYearIntermediateDiscussion || !nextYearIntermediateDiscussion
      ? 'validation.date.invalid.range'
      : 'validation.milestone.financial-report.due-date.is-not-between'
  if (minDate && maxDate) {
    return {
      message,
      minDate: minDate,
      maxDate: maxDate,
    }
  }
}

export const getProjectValidationForFinalReport = (
  milestones: Milestone[],
): ValidationForEditMilestoneDueDate | undefined => {
  const notFinalReportMilestone = milestones.filter((item) => item.type !== MILESTONE_TYPE.FINAL_REPORT).reverse()

  const beforeLastMilestone = notFinalReportMilestone.length > 0 ? notFinalReportMilestone[0] : undefined

  if (beforeLastMilestone == null) {
    return undefined
  }

  const minDate = DateUtils.parseDate(DateUtils.parseAndFormatDate(beforeLastMilestone.due_date))

  const message =
    beforeLastMilestone.type === MILESTONE_TYPE.FINANCIAL_REPORT
      ? 'validation.milestone.final-report.due-date.before'
      : 'validation.date.invalid.range.before'

  if (minDate) {
    return {
      message,
      minDate: minDate,
    }
  }
}
